import React, { useState, useEffect } from 'react';
import AppLayout from '../../components/AppLayout';
import ProspectingNav from '../../components/prospecting/ProspectingNav';
import { useTheme } from '../../contexts/ThemeContext';
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, ArrowRight, X, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LEAD_FIELDS = [
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'company', label: 'Company' },
  { value: 'title', label: 'Job Title' },
  { value: 'linkedin_url', label: 'LinkedIn URL' },
  { value: 'location', label: 'Location' },
  { value: 'industry', label: 'Industry' },
  { value: 'phone', label: 'Phone' },
  { value: 'headline', label: 'Headline/Bio' },
  { value: '', label: '-- Skip --' },
];

export default function ImportLeads() {
  const { isDark } = useTheme();
  const [step, setStep] = useState(1); // 1=upload, 2=mapping, 3=result
  const [uploading, setUploading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [uploadData, setUploadData] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchHistory(); }, []);

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospecting/import/history`, { credentials: 'include' });
      if (res.ok) setHistory(await res.json());
    } catch (err) { console.error(err); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospecting/import/upload`, { method: 'POST', credentials: 'include', body: formData });
      if (res.ok) {
        const data = await res.json();
        setUploadData(data);
        // Initialize mappings from suggestions
        const initialMappings = data.headers.map(h => {
          const suggested = data.suggested_mappings?.find(s => s.file_column === h);
          return { file_column: h, lead_field: suggested?.lead_field || '' };
        });
        setMappings(initialMappings);
        setStep(2);
      }
    } catch (err) { console.error(err); }
    finally { setUploading(false); }
  };

  const handleExecute = async () => {
    if (!uploadData) return;
    setExecuting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospecting/import/execute`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ file_id: uploadData.file_id, column_mappings: mappings.filter(m => m.lead_field) }),
      });
      if (res.ok) {
        setResult(await res.json());
        setStep(3);
        fetchHistory();
      }
    } catch (err) { console.error(err); }
    finally { setExecuting(false); }
  };

  const card = `${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border rounded-xl`;

  return (
    <AppLayout title="Import Leads">
      <div className="space-y-6" data-testid="import-leads">
        <ProspectingNav />
        <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Import Leads</h1>

        {/* Steps indicator */}
        <div className="flex items-center gap-2">
          {[1, 2, 3].map(s => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= s ? 'bg-[#A2FF00] text-black' : isDark ? 'bg-white/10 text-gray-500' : 'bg-gray-200 text-gray-400'}`}>{s}</div>
              {s < 3 && <div className={`flex-1 h-0.5 ${step > s ? 'bg-[#A2FF00]' : isDark ? 'bg-white/10' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className={`${card} p-8 text-center`}>
            <FileSpreadsheet className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
            <h2 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Upload Your Lead File</h2>
            <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Supports Excel (.xlsx) and CSV files. Maximum 50MB.</p>
            <label className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#A2FF00] text-black font-medium cursor-pointer hover:bg-[#8FE600] transition-colors" data-testid="upload-file-btn">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              {uploading ? 'Uploading...' : 'Choose File'}
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleUpload} className="hidden" />
            </label>
            <div className={`mt-6 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Need the correct format? Download a template:</p>
              <div className="flex justify-center gap-3">
                <a href={`${BACKEND_URL}/api/prospecting/import/template/csv`} download data-testid="download-csv-template" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} transition-colors`}>
                  <FileSpreadsheet className="w-4 h-4" /> CSV Template
                </a>
                <a href={`${BACKEND_URL}/api/prospecting/import/template/xlsx`} download data-testid="download-xlsx-template" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} transition-colors`}>
                  <FileSpreadsheet className="w-4 h-4" /> Excel Template
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Column Mapping */}
        {step === 2 && uploadData && (
          <div className="space-y-4">
            <div className={`${card} p-5`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Map Columns</h2>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{uploadData.filename} - {uploadData.row_count} rows</p>
                </div>
                <button onClick={() => { setStep(1); setUploadData(null); }} className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>Change file</button>
              </div>

              <div className="space-y-3">
                {mappings.map((m, i) => (
                  <div key={m.file_column} className="flex items-center gap-3">
                    <span className={`w-48 text-sm truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{m.file_column}</span>
                    <ArrowRight className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                    <select
                      value={m.lead_field}
                      onChange={e => setMappings(prev => prev.map((p, j) => j === i ? { ...p, lead_field: e.target.value } : p))}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300'} border`}
                      data-testid={`mapping-${i}`}
                    >
                      {LEAD_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {/* Preview */}
            {uploadData.preview?.length > 0 && (
              <div className={`${card} p-5`}>
                <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Preview (first {uploadData.preview.length} rows)</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                        {uploadData.headers.map((h, i) => (
                          <th key={h} className={`text-left p-2 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {uploadData.preview.map((row, ri) => (
                        <tr key={ri} className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                          {uploadData.headers.map((h, ci) => (
                            <td key={ci} className={`p-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{row[h] || ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Confirmation Summary */}
            <div className={`${card} p-5`} data-testid="import-confirmation">
              <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Confirm Import</h3>
              <div className={`grid grid-cols-3 gap-4 text-center`}>
                <div className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-blue-50'}`}>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{uploadData.row_count}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Rows</p>
                </div>
                <div className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-green-50'}`}>
                  <p className={`text-2xl font-bold text-green-500`}>{mappings.filter(m => m.lead_field).length}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Columns Mapped</p>
                </div>
                <div className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                  <p className={`text-2xl font-bold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{mappings.filter(m => !m.lead_field).length}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Skipped</p>
                </div>
              </div>
              <p className={`text-xs mt-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Duplicates (by email or LinkedIn URL) will be automatically skipped.</p>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => { setStep(1); setUploadData(null); }} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'}`}>Cancel</button>
              <button onClick={handleExecute} disabled={executing || !mappings.some(m => m.lead_field)} data-testid="execute-import-btn" className="px-6 py-2 rounded-lg text-sm bg-[#A2FF00] text-black font-medium hover:bg-[#8FE600] disabled:opacity-50">
                {executing ? <><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Importing...</> : `Confirm & Import ${uploadData.row_count} Leads`}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Result */}
        {step === 3 && result && (
          <div className={`${card} p-8 text-center`}>
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
            <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Import Complete</h2>
            <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-6">
              <div className={`${isDark ? 'bg-white/5' : 'bg-green-50'} rounded-lg p-3`}>
                <p className="text-2xl font-bold text-green-500">{result.imported}</p>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Imported</p>
              </div>
              <div className={`${isDark ? 'bg-white/5' : 'bg-yellow-50'} rounded-lg p-3`}>
                <p className="text-2xl font-bold text-yellow-500">{result.skipped_duplicates}</p>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Duplicates</p>
              </div>
              <div className={`${isDark ? 'bg-white/5' : 'bg-red-50'} rounded-lg p-3`}>
                <p className="text-2xl font-bold text-red-500">{result.errors}</p>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Errors</p>
              </div>
            </div>
            <button onClick={() => { setStep(1); setUploadData(null); setResult(null); }} className={`px-4 py-2 rounded-lg text-sm mr-3 ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'}`}>Import More</button>
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div className={`${card} p-5`}>
            <h2 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Import History</h2>
            <div className="space-y-2">
              {history.map(h => (
                <div key={h.batch_id} className={`flex items-center justify-between py-2 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                  <div>
                    <p className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{h.filename}</p>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{new Date(h.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <span className="text-green-500">{h.imported} imported</span>
                    <span className="text-yellow-500">{h.skipped_duplicates} dupes</span>
                    <span className="text-red-500">{h.errors} errors</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
